import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import ImageUpload from './ImageUpload';

const AddEditModal = ({ type, item, onSave, onClose }) => {
    const { theme } = useTheme();
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (item) {
            setFormData(item);
        } else {
            // Set default form data based on type
            switch (type) {
                case 'project':
                    setFormData({
                        title: '',
                        shortDescription: '',
                        category: 'DevOps & CI/CD',
                        techStack: [],
                        liveUrl: '',
                        githubUrl: '',
                        imageUrl: '',
                        features: [],
                        metrics: {
                            uptime: '',
                            deploymentTime: '',
                            servers: ''
                        }
                    });
                    break;
                case 'certification':
                    setFormData({
                        title: '',
                        issuer: '',
                        date: '',
                        description: '',
                        category: 'Certifications',
                        imageUrl: '',
                        validationNumber: '',
                        duration: '',
                        instructor: ''
                    });
                    break;
                case 'blog':
                    setFormData({
                        title: '',
                        excerpt: '',
                        category: 'DevOps',
                        readTime: 5,
                        url: '',
                        imageUrl: '',
                        tags: [],
                        date: new Date().toISOString().split('T')[0],
                        author: 'Vardhan Kumar Reddy'
                    });
                    break;
                case 'badge':
                    setFormData({
                        name: '',
                        icon: 'FaAws',
                        color: 'text-orange-500',
                        darkColor: 'text-orange-400',
                        order: 0
                    });
                    break;
                case 'product':
                    setFormData({
                        name: '',
                        tagline: '',
                        description: '',
                        url: '',
                        icon: 'FaRocket',
                        gradient: 'from-violet-600 via-purple-600 to-indigo-700',
                        features: [],
                        techStack: []
                    });
                    break;
                default:
                    break;
            }
        }
    }, [item, type]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Handle nested metrics for projects
        if (name.startsWith('metrics.')) {
            const metricKey = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                metrics: {
                    ...prev.metrics,
                    [metricKey]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleArrayChange = (e, fieldName) => {
        const { value } = e.target;
        const array = value.split(',').map(item => item.trim()).filter(item => item);
        setFormData(prev => ({
            ...prev,
            [fieldName]: array
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await onSave(formData);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to save. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
            <div className={`w-full max-w-3xl rounded-xl shadow-2xl p-6 my-8 max-h-[90vh] overflow-y-auto ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {item ? 'Edit' : 'Add New'} {type.charAt(0).toUpperCase() + type.slice(1)}
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-600'
                        }`}
                    >
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {type === 'project' && (
                        <ProjectForm formData={formData} setFormData={setFormData} handleChange={handleChange} handleArrayChange={handleArrayChange} theme={theme} />
                    )}
                    {type === 'certification' && (
                        <CertificationForm formData={formData} setFormData={setFormData} handleChange={handleChange} theme={theme} />
                    )}
                    {type === 'blog' && (
                        <BlogForm formData={formData} setFormData={setFormData} handleChange={handleChange} handleArrayChange={handleArrayChange} theme={theme} />
                    )}
                    {type === 'badge' && (
                        <BadgeForm formData={formData} handleChange={handleChange} theme={theme} />
                    )}
                    {type === 'product' && (
                        <ProductForm formData={formData} setFormData={setFormData} handleChange={handleChange} theme={theme} />
                    )}

                    {error && (
                        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            <FaSave /> {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Project Form Fields
const ProjectForm = ({ formData, setFormData, handleChange, handleArrayChange, theme }) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Title *</label>
                <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Project Name"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Short Description *</label>
                <textarea
                    name="shortDescription"
                    value={formData.shortDescription || ''}
                    onChange={handleChange}
                    required
                    rows="2"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Brief project description"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Category *</label>
                <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="DevOps & CI/CD">DevOps & CI/CD</option>
                    <option value="Cloud Infrastructure">Cloud Infrastructure</option>
                    <option value="Automation">Automation</option>
                    <option value="Web Development">Web Development</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Tech Stack (comma-separated) *</label>
                <input
                    type="text"
                    name="techStackString"
                    value={Array.isArray(formData.techStack) ? formData.techStack.join(', ') : (formData.techStack || '')}
                    onChange={(e) => {
                        // Store as string while typing, allow commas
                        const value = e.target.value;
                        setFormData(prev => ({
                            ...prev,
                            techStack: value
                        }));
                    }}
                    onBlur={(e) => {
                        // Convert to array only when leaving the field
                        const value = e.target.value;
                        if (value) {
                            const array = value.split(',').map(item => item.trim()).filter(item => item);
                            setFormData(prev => ({
                                ...prev,
                                techStack: array.length > 0 ? array : value
                            }));
                        }
                    }}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="AWS, Docker, Jenkins"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Live URL</label>
                <input
                    type="url"
                    name="liveUrl"
                    value={formData.liveUrl || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="https://example.com"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>GitHub URL</label>
                <input
                    type="url"
                    name="githubUrl"
                    value={formData.githubUrl || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="https://github.com/..."
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Key Features</label>
                
                {/* Display existing features */}
                <div className="space-y-2 mb-3">
                    {Array.isArray(formData.features) && formData.features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={feature}
                                onChange={(e) => {
                                    const newFeatures = [...formData.features];
                                    newFeatures[index] = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        features: newFeatures
                                    }));
                                }}
                                className={`flex-1 px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                                placeholder={`Feature ${index + 1}`}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const newFeatures = formData.features.filter((_, i) => i !== index);
                                    setFormData(prev => ({
                                        ...prev,
                                        features: newFeatures
                                    }));
                                }}
                                className={`px-4 py-2 rounded-lg transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-red-600 hover:bg-red-700 text-white'
                                        : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
                
                {/* Add feature button */}
                <button
                    type="button"
                    onClick={() => {
                        const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
                        setFormData(prev => ({
                            ...prev,
                            features: [...currentFeatures, '']
                        }));
                    }}
                    className={`w-full px-4 py-2 rounded-lg border-2 border-dashed transition-colors ${
                        theme === 'dark'
                            ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400'
                            : 'border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600'
                    }`}
                >
                    + Add Feature
                </button>
            </div>

            <div className="md:col-span-2">
                <ImageUpload
                    value={formData.imageUrl || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    folder="projects"
                    theme={theme}
                    label="Project Image"
                />
            </div>

            {/* Dynamic Metrics based on Category */}
            {formData.category === 'DevOps & CI/CD' && (
                <>
                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Uptime</label>
                        <input
                            type="text"
                            name="metrics.uptime"
                            value={formData.metrics?.uptime || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="99.9%"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Deployment Time</label>
                        <input
                            type="text"
                            name="metrics.deploymentTime"
                            value={formData.metrics?.deploymentTime || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="5 mins"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Servers Managed</label>
                        <input
                            type="text"
                            name="metrics.servers"
                            value={formData.metrics?.servers || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="10+"
                        />
                    </div>
                </>
            )}

            {formData.category === 'Cloud Infrastructure' && (
                <>
                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Cloud Provider</label>
                        <input
                            type="text"
                            name="metrics.cloudProvider"
                            value={formData.metrics?.cloudProvider || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="AWS, Azure, GCP"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Infrastructure Scale</label>
                        <input
                            type="text"
                            name="metrics.scale"
                            value={formData.metrics?.scale || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="Multi-region, 50+ instances"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Cost Optimization</label>
                        <input
                            type="text"
                            name="metrics.costSavings"
                            value={formData.metrics?.costSavings || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="40% cost reduction"
                        />
                    </div>
                </>
            )}

            {formData.category === 'Automation' && (
                <>
                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Time Saved</label>
                        <input
                            type="text"
                            name="metrics.timeSaved"
                            value={formData.metrics?.timeSaved || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="80% time reduction"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Tasks Automated</label>
                        <input
                            type="text"
                            name="metrics.tasksAutomated"
                            value={formData.metrics?.tasksAutomated || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="20+ manual tasks"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Error Reduction</label>
                        <input
                            type="text"
                            name="metrics.errorReduction"
                            value={formData.metrics?.errorReduction || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="95% fewer errors"
                        />
                    </div>
                </>
            )}

            {formData.category === 'Web Development' && (
                <>
                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Performance Score</label>
                        <input
                            type="text"
                            name="metrics.performance"
                            value={formData.metrics?.performance || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="95/100 Lighthouse"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Load Time</label>
                        <input
                            type="text"
                            name="metrics.loadTime"
                            value={formData.metrics?.loadTime || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="under 2 seconds"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Monthly Users</label>
                        <input
                            type="text"
                            name="metrics.users"
                            value={formData.metrics?.users || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="10,000+ users"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Monthly Visitors</label>
                        <input
                            type="text"
                            name="metrics.visitors"
                            value={formData.metrics?.visitors || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="50,000+ visitors"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Hosting Platform</label>
                        <input
                            type="text"
                            name="metrics.hosting"
                            value={formData.metrics?.hosting || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="AWS / Vercel / Netlify"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>Responsive Design</label>
                        <input
                            type="text"
                            name="metrics.responsive"
                            value={formData.metrics?.responsive || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="Mobile-First / Desktop-First"
                        />
                    </div>
                </>
            )}
        </div>
    </>
);

// Certification Form Fields
const CertificationForm = ({ formData, setFormData, handleChange, theme }) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Title *</label>
                <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Certification Name"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Issuer *</label>
                <input
                    type="text"
                    name="issuer"
                    value={formData.issuer || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="AWS, Udemy, etc."
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Date *</label>
                <input
                    type="date"
                    name="date"
                    value={formData.date || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Instructor</label>
                <input
                    type="text"
                    name="instructor"
                    value={formData.instructor || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Instructor name"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Duration</label>
                <input
                    type="text"
                    name="duration"
                    value={formData.duration || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="20 hours"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Validation Number</label>
                <input
                    type="text"
                    name="validationNumber"
                    value={formData.validationNumber || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Certificate ID"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Description *</label>
                <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    required
                    rows="3"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Brief description of the certification"
                />
            </div>

            <div className="md:col-span-2">
                <ImageUpload
                    value={formData.imageUrl || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    folder="certifications"
                    theme={theme}
                    label="Certificate Image"
                />
            </div>
        </div>
    </>
);

// Blog Form Fields
const BlogForm = ({ formData, setFormData, handleChange, handleArrayChange, theme }) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Title *</label>
                <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Blog Post Title"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Excerpt *</label>
                <textarea
                    name="excerpt"
                    value={formData.excerpt || ''}
                    onChange={handleChange}
                    required
                    rows="2"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Brief summary of the blog post"
                />
            </div>
            <div className="md:col-span-2">
                <ImageUpload
                    value={formData.imageUrl || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    folder="blogs"
                    theme={theme}
                    label="Featured Image"
                />
            </div>
            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Category *</label>
                <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="DevOps">DevOps</option>
                    <option value="Tutorial">Tutorial</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Cloud">Cloud</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Read Time (minutes) *</label>
                <input
                    type="number"
                    name="readTime"
                    value={formData.readTime || ''}
                    onChange={handleChange}
                    required
                    min="1"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="5"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Medium URL *</label>
                <input
                    type="url"
                    name="url"
                    value={formData.url || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="https://medium.com/@..."
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Tags (comma-separated) *</label>
                <input
                    type="text"
                    name="tagsString"
                    value={Array.isArray(formData.tags) ? formData.tags.join(', ') : (formData.tags || '')}
                    onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                            ...prev,
                            tags: value
                        }));
                    }}
                    onBlur={(e) => {
                        const value = e.target.value;
                        if (value) {
                            const array = value.split(',').map(item => item.trim()).filter(item => item);
                            setFormData(prev => ({
                                ...prev,
                                tags: array.length > 0 ? array : value
                            }));
                        }
                    }}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="DevOps, Docker, AWS"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Date *</label>
                <input
                    type="date"
                    name="date"
                    value={formData.date || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
            </div>
        </div>
    </>
);

// Badge Form Fields
const BadgeForm = ({ formData, handleChange, theme }) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Badge Name *</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g. AWS Certified"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Icon *</label>
                <select
                    name="icon"
                    value={formData.icon || 'FaAws'}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="FaAws">AWS</option>
                    <option value="SiKubernetes">Kubernetes</option>
                    <option value="SiTerraform">Terraform</option>
                    <option value="FaDocker">Docker</option>
                    <option value="FaPython">Python</option>
                    <option value="FaJenkins">Jenkins</option>
                    <option value="FaLinux">Linux</option>
                    <option value="FaJava">Java</option>
                    <option value="FaCloud">Cloud</option>
                    <option value="SiGooglecloud">Google Cloud</option>
                    <option value="SiAnsible">Ansible</option>
                    <option value="SiPrometheus">Prometheus</option>
                    <option value="SiGrafana">Grafana</option>
                    <option value="FaMicrosoft">Azure / Microsoft</option>
                    <option value="FaCertificate">Certificate</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Color (Light Mode) *</label>
                <select
                    name="color"
                    value={formData.color || 'text-orange-500'}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="text-orange-500">Orange</option>
                    <option value="text-blue-600">Blue</option>
                    <option value="text-purple-600">Purple</option>
                    <option value="text-green-600">Green</option>
                    <option value="text-red-600">Red</option>
                    <option value="text-cyan-600">Cyan</option>
                    <option value="text-yellow-500">Yellow</option>
                    <option value="text-pink-600">Pink</option>
                    <option value="text-gray-800">Dark Gray</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Color (Dark Mode) *</label>
                <select
                    name="darkColor"
                    value={formData.darkColor || 'text-orange-400'}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="text-orange-400">Orange</option>
                    <option value="text-blue-400">Blue</option>
                    <option value="text-purple-400">Purple</option>
                    <option value="text-green-400">Green</option>
                    <option value="text-red-400">Red</option>
                    <option value="text-cyan-400">Cyan</option>
                    <option value="text-yellow-400">Yellow</option>
                    <option value="text-pink-400">Pink</option>
                    <option value="text-gray-300">Light Gray</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Display Order</label>
                <input
                    type="number"
                    name="order"
                    value={formData.order || 0}
                    onChange={handleChange}
                    min="0"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="0"
                />
                <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>Lower number = appears first</p>
            </div>
        </div>
    </>
);

// Product Form Fields
const ProductForm = ({ formData, setFormData, handleChange, theme }) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Product Name *</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g. Stride"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Tagline *</label>
                <input
                    type="text"
                    name="tagline"
                    value={formData.tagline || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g. AI-Native Project Management"
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Description *</label>
                <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    required
                    rows="3"
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Describe what the product does..."
                />
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Product URL *</label>
                <input
                    type="url"
                    name="url"
                    value={formData.url || ''}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="https://yourproduct.com"
                />
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Icon</label>
                <select
                    name="icon"
                    value={formData.icon || 'FaRocket'}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="FaRocket">Rocket</option>
                    <option value="FaProjectDiagram">Project Diagram</option>
                    <option value="FaMicrophone">Microphone</option>
                    <option value="FaBrain">Brain / AI</option>
                    <option value="FaChartLine">Chart / Analytics</option>
                    <option value="FaShieldAlt">Shield / Security</option>
                    <option value="FaUsers">Users / Team</option>
                    <option value="SiOpenai">OpenAI</option>
                </select>
            </div>

            <div>
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Color Theme</label>
                <select
                    name="gradient"
                    value={formData.gradient || 'from-violet-600 via-purple-600 to-indigo-700'}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="from-violet-600 via-purple-600 to-indigo-700">Purple</option>
                    <option value="from-emerald-500 via-teal-600 to-cyan-700">Teal</option>
                    <option value="from-blue-600 via-blue-700 to-indigo-800">Blue</option>
                    <option value="from-orange-500 via-red-500 to-pink-600">Orange-Red</option>
                    <option value="from-rose-500 via-pink-600 to-purple-700">Pink</option>
                    <option value="from-cyan-500 via-blue-600 to-violet-700">Cyan-Violet</option>
                </select>
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Features</label>
                <div className="space-y-2 mb-3">
                    {Array.isArray(formData.features) && formData.features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={feature}
                                onChange={(e) => {
                                    const newFeatures = [...formData.features];
                                    newFeatures[index] = e.target.value;
                                    setFormData(prev => ({ ...prev, features: newFeatures }));
                                }}
                                className={`flex-1 px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                                placeholder={`Feature ${index + 1}`}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const newFeatures = formData.features.filter((_, i) => i !== index);
                                    setFormData(prev => ({ ...prev, features: newFeatures }));
                                }}
                                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        const current = Array.isArray(formData.features) ? formData.features : [];
                        setFormData(prev => ({ ...prev, features: [...current, ''] }));
                    }}
                    className={`w-full px-4 py-2 rounded-lg border-2 border-dashed transition-colors ${
                        theme === 'dark'
                            ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400'
                            : 'border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600'
                    }`}
                >
                    + Add Feature
                </button>
            </div>

            <div className="md:col-span-2">
                <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Tech Stack (comma-separated)</label>
                <input
                    type="text"
                    name="techStackString"
                    value={Array.isArray(formData.techStack) ? formData.techStack.join(', ') : (formData.techStack || '')}
                    onChange={(e) => {
                        setFormData(prev => ({ ...prev, techStack: e.target.value }));
                    }}
                    onBlur={(e) => {
                        const value = e.target.value;
                        if (value) {
                            const array = value.split(',').map(item => item.trim()).filter(item => item);
                            setFormData(prev => ({ ...prev, techStack: array.length > 0 ? array : value }));
                        }
                    }}
                    className={`w-full px-4 py-2 rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="AI/ML, React, Python, Cloud"
                />
            </div>
        </div>
    </>
);

export default AddEditModal;
